import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ShareCard from './components/ShareCard';
import ShareCreationWizard from './components/ShareCreationWizard';
import ShareDetailsModal from './components/ShareDetailsModal';
import BulkShareModal from './components/BulkShareModal';

const SharingManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('internal');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShares, setSelectedShares] = useState([]);
  const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock data for internal shares
  const [internalShares, setInternalShares] = useState([
    {
      id: 1,
      type: 'internal',
      recipientName: 'John Smith',
      recipientEmail: 'john.smith@company.com',
      department: 'Engineering',
      itemCount: 5,
      accessLevel: 'contributor',
      status: 'active',
      expiresAt: '2025-02-15T23:59:59Z',
      usageCount: 12,
      usageLimit: null,
      lastAccess: '2025-01-03T14:30:00Z',
      createdAt: '2025-01-01T10:00:00Z'
    },
    {
      id: 2,
      type: 'internal',
      recipientName: 'Sarah Johnson',
      recipientEmail: 'sarah.johnson@company.com',
      department: 'Marketing',
      itemCount: 3,
      accessLevel: 'viewer',
      status: 'active',
      expiresAt: null,
      usageCount: 8,
      usageLimit: null,
      lastAccess: '2025-01-02T16:45:00Z',
      createdAt: '2024-12-28T14:20:00Z'
    },
    {
      id: 3,
      type: 'internal',
      recipientName: 'Mike Davis',
      recipientEmail: 'mike.davis@company.com',
      department: 'Sales',
      itemCount: 2,
      accessLevel: 'viewer',
      status: 'pending',
      expiresAt: '2025-01-10T23:59:59Z',
      usageCount: 0,
      usageLimit: 10,
      lastAccess: null,
      createdAt: '2025-01-03T09:15:00Z'
    }
  ]);

  // Mock data for external shares
  const [externalShares, setExternalShares] = useState([
    {
      id: 4,
      type: 'external',
      recipientName: 'Alex Partner',
      recipientEmail: 'alex@partner-company.com',
      department: null,
      itemCount: 2,
      accessLevel: 'viewer',
      status: 'active',
      expiresAt: '2025-01-07T23:59:59Z',
      usageCount: 3,
      usageLimit: 5,
      lastAccess: '2025-01-03T11:20:00Z',
      createdAt: '2025-01-02T13:30:00Z'
    },
    {
      id: 5,
      type: 'external',
      recipientName: 'Lisa Contractor',
      recipientEmail: 'lisa@freelancer.com',
      department: null,
      itemCount: 1,
      accessLevel: 'viewer',
      status: 'expired',
      expiresAt: '2025-01-02T23:59:59Z',
      usageCount: 2,
      usageLimit: 3,
      lastAccess: '2025-01-02T18:45:00Z',
      createdAt: '2024-12-30T16:00:00Z'
    }
  ]);

  const currentShares = activeTab === 'internal' ? internalShares : externalShares;
  const filteredShares = currentShares?.filter(share =>
    share?.recipientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    share?.recipientEmail?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const activeSharesCount = currentShares?.filter(share => share?.status === 'active')?.length;
  const expiringSoonCount = currentShares?.filter(share => {
    if (!share?.expiresAt || share?.status !== 'active') return false;
    const expiryDate = new Date(share.expiresAt);
    const now = new Date();
    const diffHours = (expiryDate - now) / (1000 * 60 * 60);
    return diffHours <= 24 && diffHours > 0;
  })?.length;

  useEffect(() => {
    // Simulate real-time notifications
    const mockNotifications = [
      {
        id: 1,
        type: 'warning',
        message: 'Share with Alex Partner expires in 4 hours',
        timestamp: new Date()
      },
      {
        id: 2,
        type: 'success',
        message: 'New share accepted by John Smith',
        timestamp: new Date(Date.now() - 300000)
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  const handleShareSelection = (shareId, isSelected) => {
    if (isSelected) {
      const share = currentShares?.find(s => s?.id === shareId);
      setSelectedShares([...selectedShares, share]);
    } else {
      setSelectedShares(selectedShares?.filter(s => s?.id !== shareId));
    }
  };

  const handleSelectAll = () => {
    if (selectedShares?.length === filteredShares?.length) {
      setSelectedShares([]);
    } else {
      setSelectedShares([...filteredShares]);
    }
  };

  const handleCreateShare = (shareData) => {
    const newShare = {
      id: Date.now(),
      ...shareData,
      status: 'pending',
      usageCount: 0,
      lastAccess: null,
      createdAt: new Date()?.toISOString(),
      recipientName: shareData?.type === 'internal' ? 'New User' : 'Guest User',
      recipientEmail: 'user@example.com',
      itemCount: shareData?.items?.length
    };

    if (shareData?.type === 'internal') {
      setInternalShares([...internalShares, newShare]);
    } else {
      setExternalShares([...externalShares, newShare]);
    }

    // Add success notification
    setNotifications([
      {
        id: Date.now(),
        type: 'success',
        message: `Share created successfully for ${newShare?.recipientName}`,
        timestamp: new Date()
      },
      ...notifications
    ]);
  };

  const handleRevokeShare = (shareId) => {
    const updateShares = (shares) =>
      shares?.map(share =>
        share?.id === shareId ? { ...share, status: 'revoked' } : share
      );

    setInternalShares(updateShares(internalShares));
    setExternalShares(updateShares(externalShares));
    setSelectedShares(selectedShares?.filter(s => s?.id !== shareId));

    // Add notification
    setNotifications([
      {
        id: Date.now(),
        type: 'warning',
        message: 'Share access has been revoked',
        timestamp: new Date()
      },
      ...notifications
    ]);
  };

  const handleExtendShare = (shareId) => {
    const extendDate = new Date();
    extendDate?.setDate(extendDate?.getDate() + 30);

    const updateShares = (shares) =>
      shares?.map(share =>
        share?.id === shareId
          ? { ...share, expiresAt: extendDate?.toISOString(), status: 'active' }
          : share
      );

    setInternalShares(updateShares(internalShares));
    setExternalShares(updateShares(externalShares));

    // Add notification
    setNotifications([
      {
        id: Date.now(),
        type: 'success',
        message: 'Share access has been extended by 30 days',
        timestamp: new Date()
      },
      ...notifications
    ]);
  };

  const handleViewDetails = (share) => {
    setSelectedShare(share);
    setIsDetailsModalOpen(true);
  };

  const handleBulkShare = (actionData) => {
    const { action, shareIds, settings } = actionData;
    
    const updateShares = (shares) =>
      shares?.map(share => {
        if (!shareIds?.includes(share?.id)) return share;
        
        switch (action) {
          case 'extend':
            return {
              ...share,
              expiresAt: settings?.expiresAt,
              status: 'active'
            };
          case 'revoke':
            return { ...share, status: 'revoked' };
          case 'modify':
            return {
              ...share,
              accessLevel: settings?.accessLevel || share?.accessLevel,
              usageLimit: settings?.usageLimit || share?.usageLimit
            };
          default:
            return share;
        }
      });

    setInternalShares(updateShares(internalShares));
    setExternalShares(updateShares(externalShares));
    setSelectedShares([]);

    // Add notification
    setNotifications([
      {
        id: Date.now(),
        type: 'success',
        message: `Bulk ${action} applied to ${shareIds?.length} share${shareIds?.length !== 1 ? 's' : ''}`,
        timestamp: new Date()
      },
      ...notifications
    ]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
      } pt-16`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Sharing Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage secure credential sharing with internal teams and external partners
                </p>
              </div>
              <Button onClick={() => setIsCreateWizardOpen(true)}>
                <Icon name="Plus" size={20} className="mr-2" />
                Create Share
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Active Shares</p>
                    <p className="text-2xl font-bold text-foreground">{activeSharesCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Share2" size={24} className="text-success" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Expiring Soon</p>
                    <p className="text-2xl font-bold text-foreground">{expiringSoonCount}</p>
                  </div>
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-warning" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Internal Shares</p>
                    <p className="text-2xl font-bold text-foreground">{internalShares?.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-primary" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">External Shares</p>
                    <p className="text-2xl font-bold text-foreground">{externalShares?.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="ExternalLink" size={24} className="text-accent" />
                  </div>
                </div>
              </div>
            </div>

            {/* Notifications */}
            {notifications?.length > 0 && (
              <div className="mb-6 space-y-2">
                {notifications?.slice(0, 2)?.map((notification) => (
                  <div
                    key={notification?.id}
                    className={`flex items-center space-x-3 p-4 rounded-lg border ${
                      notification?.type === 'warning' ?'bg-warning/10 border-warning/20 text-warning' :'bg-success/10 border-success/20 text-success'
                    }`}
                  >
                    <Icon
                      name={notification?.type === 'warning' ? 'AlertTriangle' : 'CheckCircle'}
                      size={20}
                    />
                    <span className="text-sm font-medium">{notification?.message}</span>
                    <span className="text-xs opacity-75 ml-auto">
                      {notification?.timestamp?.toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              {/* Tab Navigation */}
              <div className="flex bg-muted rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('internal')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeTab === 'internal' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Users" size={16} className="mr-2" />
                  Internal ({internalShares?.length})
                </button>
                <button
                  onClick={() => setActiveTab('external')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeTab === 'external' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="ExternalLink" size={16} className="mr-2" />
                  External ({externalShares?.length})
                </button>
              </div>

              {/* Bulk Actions */}
              {selectedShares?.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedShares?.length} selected
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsBulkModalOpen(true)}
                  >
                    <Icon name="Settings" size={16} className="mr-2" />
                    Bulk Actions
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {/* Search */}
              <div className="relative">
                <Input
                  type="search"
                  placeholder="Search shares..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10 w-64"
                />
                <Icon
                  name="Search"
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                />
              </div>

              {/* Select All */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                <Icon name="CheckSquare" size={16} className="mr-2" />
                {selectedShares?.length === filteredShares?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>

          {/* Shares Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredShares?.map((share) => (
              <div key={share?.id} className="relative">
                <div className="absolute top-4 left-4 z-10">
                  <input
                    type="checkbox"
                    checked={selectedShares?.some(s => s?.id === share?.id)}
                    onChange={(e) => handleShareSelection(share?.id, e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-card border-border rounded focus:ring-primary focus:ring-2"
                  />
                </div>
                <ShareCard
                  share={share}
                  type={activeTab}
                  onRevoke={handleRevokeShare}
                  onViewDetails={handleViewDetails}
                />
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredShares?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Share2" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {searchQuery ? 'No shares found' : `No ${activeTab} shares yet`}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : `Create your first ${activeTab} share to get started`
                }
              </p>
              {!searchQuery && (
                <Button onClick={() => setIsCreateWizardOpen(true)}>
                  <Icon name="Plus" size={20} className="mr-2" />
                  Create Share
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <ShareCreationWizard
        isOpen={isCreateWizardOpen}
        onClose={() => setIsCreateWizardOpen(false)}
        onCreateShare={handleCreateShare}
      />
      <ShareDetailsModal
        share={selectedShare}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        onRevoke={handleRevokeShare}
        onExtend={handleExtendShare}
      />
      <BulkShareModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onBulkShare={handleBulkShare}
        selectedShares={selectedShares}
      />
    </div>
  );
};

export default SharingManagement;